import { Component, DestroyRef, effect, inject, signal, untracked } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { BaseChartDirective } from 'ng2-charts';
import {
  CategoryScale,
  Chart,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  BarController,
  BarElement,
} from 'chart.js';
import { AnalyticsService } from '../../core/services/analytics.service';
import { DateRangeService } from '../../core/services/date-range.service';
import { AnalyticsTrendBundle } from '../../core/models/trends.models';
import { MetricCardComponent } from '../../shared/components/metric-card/metric-card.component';

Chart.register(
  LineController,
  LineElement,
  PointElement,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Legend,
  Tooltip,
);

@Component({
  selector: 'app-trends-page',
  standalone: true,
  imports: [DecimalPipe, FormsModule, BaseChartDirective, MetricCardComponent],
  templateUrl: './trends-page.component.html',
  styleUrl: './trends-page.component.scss',
})
export class TrendsPageComponent {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly dateRange = inject(DateRangeService);
  private readonly destroyRef = inject(DestroyRef);
  private currentLoadId = 0;

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly data = signal<AnalyticsTrendBundle | null>(null);
  readonly interval = signal<'day' | 'week'>('week');

  readonly issueVolumeChartData = signal({
    labels: [] as string[],
    datasets: [
      { label: 'Total', data: [] as number[], borderColor: '#1d4ed8', backgroundColor: 'rgba(29,78,216,0.08)', tension: 0.3, fill: true },
      { label: 'High', data: [] as number[], borderColor: '#dc2626', backgroundColor: 'transparent', tension: 0.3 },
      { label: 'Medium', data: [] as number[], borderColor: '#f59e0b', backgroundColor: 'transparent', tension: 0.3 },
    ],
  });

  readonly blockRateChartData = signal({
    labels: [] as string[],
    datasets: [
      { label: 'Block Rate %', data: [] as number[], borderColor: '#dc2626', backgroundColor: 'rgba(220,38,38,0.08)', tension: 0.3, fill: true },
    ],
  });

  readonly prVolumeChartData = signal({
    labels: [] as string[],
    datasets: [
      { label: 'PRs', data: [] as number[], backgroundColor: '#1d4ed8', borderRadius: 8, borderSkipped: false as const },
      { label: 'Review Runs', data: [] as number[], backgroundColor: '#93c5fd', borderRadius: 8, borderSkipped: false as const },
    ],
  });

  readonly avgIssuesChartData = signal({
    labels: [] as string[],
    datasets: [
      { label: 'Avg Issues / PR', data: [] as number[], borderColor: '#7c3aed', backgroundColor: 'rgba(124,58,237,0.08)', tension: 0.3, fill: true },
    ],
  });

  readonly lineChartOptions = {
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom' as const } },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
  };

  readonly barChartOptions = {
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom' as const } },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
  };

  constructor() {
    effect(() => {
      this.dateRange.appliedRange();
      this.dateRange.appliedRevision();
      untracked(() => this.load());
    });
  }

  load(): void {
    const loadId = ++this.currentLoadId;
    this.loading.set(true);
    this.error.set(null);
    this.data.set(null);

    this.analyticsService
      .getTrends({ ...this.dateRange.toQueryParams(), interval: this.interval() })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          if (loadId === this.currentLoadId) this.loading.set(false);
        }),
      )
      .subscribe({
        next: (response) => {
          if (loadId !== this.currentLoadId) return;
          this.applyTrends(response);
        },
        error: (err: unknown) => {
          if (loadId !== this.currentLoadId) return;
          console.error('Trends request failed', err);
          this.error.set(this.formatError(err, 'Trends data could not be loaded.'));
        },
      });
  }

  changeInterval(value: 'day' | 'week'): void {
    this.interval.set(value);
    this.load();
  }

  get latestPoint() {
    const points = this.data()?.points;
    return points?.length ? points[points.length - 1] : null;
  }

  private applyTrends(bundle: AnalyticsTrendBundle): void {
    this.data.set(bundle);
    const labels = bundle.points.map((p) => p.periodStart.slice(0, 10));

    this.issueVolumeChartData.set({
      labels,
      datasets: [
        { ...this.issueVolumeChartData().datasets[0], data: bundle.points.map((p) => p.totalIssues) },
        { ...this.issueVolumeChartData().datasets[1], data: bundle.points.map((p) => p.highIssues) },
        { ...this.issueVolumeChartData().datasets[2], data: bundle.points.map((p) => p.mediumIssues) },
      ],
    });

    this.blockRateChartData.set({
      labels,
      datasets: [
        { ...this.blockRateChartData().datasets[0], data: bundle.points.map((p) => p.blockRate) },
      ],
    });

    this.prVolumeChartData.set({
      labels,
      datasets: [
        { ...this.prVolumeChartData().datasets[0], data: bundle.points.map((p) => p.pullRequestCount) },
        { ...this.prVolumeChartData().datasets[1], data: bundle.points.map((p) => p.reviewRunCount) },
      ],
    });

    this.avgIssuesChartData.set({
      labels,
      datasets: [
        { ...this.avgIssuesChartData().datasets[0], data: bundle.points.map((p) => p.avgIssuesPerPullRequest) },
      ],
    });
  }

  private formatError(error: unknown, fallback: string): string {
    if ((error as { name?: string })?.name === 'TimeoutError') return 'Request timed out after 8s.';
    if (error instanceof HttpErrorResponse) {
      if (error.status === 0) return `Cannot reach backend${error.url ? ` @ ${error.url}` : ''}.`;
      return `${fallback} (${error.status}: ${error.message})`;
    }
    return fallback;
  }
}
